/**
 * Customer wishlist — external API (https://api.admin.dev.tgcksa.space).
 */
(function () {
  const config = window.theme?.wishlist;
  if (!config?.enabled) return;

  const SELECTOR_TOGGLE = 'wishlist-toggle';
  const SELECTOR_COUNT = '[data-wishlist-count]';
  const SOURCE = config.source || 'SHOPIFY_STOREFRONT';

  function parseIds(csv) {
    return new Set(
      (csv || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map(Number)
        .filter((n) => !Number.isNaN(n))
    );
  }

  function idsToCsv(set) {
    return [...set].join(',');
  }

  function numericIdFromGid(gid) {
    const match = String(gid || '').match(/Product\/(\d+)/);
    return match ? Number(match[1]) : null;
  }

  function gidFromNumericId(id) {
    return `gid://shopify/Product/${id}`;
  }

  function unwrapData(payload) {
    if (payload == null) return payload;
    if (Object.prototype.hasOwnProperty.call(payload, 'data')) return payload.data;
    return payload;
  }

  function imageUrlsFromItem(item, nested) {
    const urls = item?.productImagesUrl || item?.product_images_url;
    if (Array.isArray(urls) && urls.length) return urls.filter(Boolean);
    const single =
      nested.image ||
      nested.featured_image ||
      nested.featuredImage?.url ||
      item?.image ||
      null;
    return single ? [single] : [];
  }

  function productHandleFromItem(item, nested) {
    const handle =
      item?.handle ||
      item?.productHandle ||
      item?.product_handle ||
      nested.handle ||
      null;
    const normalized = handle == null ? '' : String(handle).trim();
    return normalized || null;
  }

  function storefrontRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  async function fetchStorefrontProduct(handle) {
    if (!handle) return null;

    try {
      const response = await fetch(
        `${storefrontRoot()}products/${encodeURIComponent(handle)}.js`,
        { credentials: 'same-origin' }
      );
      if (!response.ok) return null;
      return response.json();
    } catch (err) {
      console.warn('[wishlist] product fetch failed:', handle, err);
      return null;
    }
  }

  async function localizeWishlistItems(items) {
    return Promise.all(
      items.map(async (item) => {
        if (!item.handle) return item;

        const product = await fetchStorefrontProduct(item.handle);
        if (!product) return item;

        const variant = product.variants?.[0];
        const images = Array.isArray(product.images) ? product.images.filter(Boolean) : item.images;

        return {
          ...item,
          title: product.title || item.title,
          url: `${storefrontRoot()}products/${product.handle}`,
          image: product.featured_image || images[0] || item.image,
          images: images.length ? images : item.images,
          price: variant?.price ?? item.price,
          priceInMajorUnits: false,
        };
      })
    );
  }

  function parseWishlistItems(payload) {
    const data = unwrapData(payload);
    const items = Array.isArray(data) ? data : [];
    return items
      .map((item) => {
        const gid =
          item?.shopifyProductId ||
          item?.productId ||
          item?.product_id ||
          item?.productGid;
        const nested = item?.product || item?.shopifyProduct || item?.shopify_product || {};
        const numericId =
          numericIdFromGid(gid) ||
          Number(item?.productNumericId || item?.product_numeric_id || nested.id);
        const title =
          item?.productTitle ||
          item?.product_title ||
          nested.title ||
          item?.title ||
          null;
        const price = Number(item?.price ?? nested.price ?? 0) || 0;
        const images = imageUrlsFromItem(item, nested);
        const image = images[0] || null;

        const handle = productHandleFromItem(item, nested);

        return {
          gid,
          numericId: Number.isNaN(numericId) ? null : numericId,
          handle,
          title,
          url: nested.url || item?.url || nested.onlineStoreUrl || null,
          image,
          images,
          price,
          priceInMajorUnits: Boolean(item?.productTitle || item?.product_title || item?.shopifyProductId),
        };
      })
      .filter((item) => item.gid || item.numericId);
  }

  function productIdsFromItems(items) {
    return items.map((item) => item.numericId).filter((id) => id != null);
  }

  class WishlistApi {
    constructor({ apiBase, customerId, customerEmail, source }) {
      this.apiBase = String(apiBase || '').replace(/\/$/, '');
      this.customerId = customerId ? String(customerId) : '';
      this.customerEmail = customerEmail ? String(customerEmail) : '';
      this.source = source || SOURCE;
    }

    get ready() {
      return Boolean(this.apiBase && this.customerId);
    }

    async request(path, options = {}) {
      const headers = {
        Accept: 'application/json',
        ...(options.headers || {}),
      };
      if (options.body) {
        headers['Content-Type'] = 'application/json';
      }

      const res = await fetch(`${this.apiBase}${path}`, {
        ...options,
        headers,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const errMsg = data?.message || data?.error || 'Wishlist request failed';
        const err = new Error(errMsg);
        err.status = res.status;
        throw err;
      }
      return data;
    }

    async getList() {
      const params = new URLSearchParams({
        shopifyCustomerId: this.customerId,
        source: this.source,
      });
      const payload = await this.request(`/wishlist?${params}`, { method: 'GET' });
      return parseWishlistItems(payload);
    }

    async getCount() {
      const params = new URLSearchParams({
        shopifyCustomerId: this.customerId,
        source: this.source,
      });
      const payload = await this.request(`/wishlist/count?${params}`, { method: 'GET' });
      const data = unwrapData(payload);
      if (typeof data?.count === 'number') return data.count;
      if (typeof payload?.count === 'number') return payload.count;
      return 0;
    }

    async check(productGid) {
      const params = new URLSearchParams({
        shopifyCustomerId: this.customerId,
        productId: productGid,
      });
      const payload = await this.request(`/wishlist/check?${params}`, { method: 'GET' });
      const data = unwrapData(payload);
      if (typeof data?.isWishlisted === 'boolean') return data.isWishlisted;
      if (typeof payload?.isWishlisted === 'boolean') return payload.isWishlisted;
      return false;
    }

    async add(productGid) {
      return this.request('/wishlist', {
        method: 'POST',
        body: JSON.stringify({
          shopifyCustomerId: this.customerId,
          email: this.customerEmail,
          productId: productGid,
          source: this.source,
        }),
      });
    }

    async remove(productGids) {
      const ids = Array.isArray(productGids) ? productGids : [productGids];
      return this.request('/wishlist', {
        method: 'DELETE',
        body: JSON.stringify({
          shopifyCustomerId: this.customerId,
          productIds: ids,
        }),
      });
    }
  }

  const api = new WishlistApi({
    apiBase: config.apiBase,
    customerId: config.customerId,
    customerEmail: config.customerEmail,
    source: config.source,
  });

  function updateCountBadges(count) {
    document.querySelectorAll(SELECTOR_COUNT).forEach((el) => {
      const accountLink = el.closest('.account-wishlist-link');
      if (accountLink) {
        el.textContent = count > 0 ? ` (${count})` : '';
        el.hidden = count < 1;
        return;
      }

      el.textContent = count > 0 ? String(count) : '';
      el.hidden = count < 1;
      const link = el.closest('.header-wishlist-link');
      if (link) {
        link.classList.toggle('header-wishlist-link--has-items', count > 0);
        const baseLabel = link.getAttribute('data-aria-label-base');
        const withCountTemplate = link.getAttribute('data-aria-label-with-count');
        if (withCountTemplate) {
          link.setAttribute(
            'aria-label',
            count > 0 ? withCountTemplate.replace('[count]', count) : baseLabel || ''
          );
        } else if (baseLabel) {
          link.setAttribute('aria-label', baseLabel);
        }
      }
    });
  }

  function dispatchUpdated(detail) {
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail, bubbles: true }));
    if (typeof detail.count === 'number') updateCountBadges(detail.count);
  }

  function syncAllToggles(productIds) {
    const csv = productIds.join(',');
    document.querySelectorAll(SELECTOR_TOGGLE).forEach((el) => {
      el.dataset.wishlistIds = csv;
      el.ids = parseIds(csv);
      if (typeof el.syncUi === 'function') el.syncUi(false);
    });
  }

  function applyProductWishlistState(productId, isWishlisted, announce) {
    document.querySelectorAll(SELECTOR_TOGGLE).forEach((el) => {
      if (Number(el.dataset.productId) !== Number(productId)) return;
      const ids = parseIds(el.dataset.wishlistIds);
      if (isWishlisted) ids.add(productId);
      else ids.delete(productId);
      el.dataset.wishlistIds = idsToCsv(ids);
      el.ids = ids;
      if (typeof el.syncUi === 'function') el.syncUi(!!announce);
    });
  }

  async function bootstrapWishlist() {
    if (!api.ready) {
      return { items: [], error: null, skipHydration: true };
    }

    try {
      const [items, count] = await Promise.all([api.getList(), api.getCount()]);
      const productIds = productIdsFromItems(items);
      syncAllToggles(productIds);
      updateCountBadges(count);
      dispatchUpdated({ productIds, count, saved: null, productId: null });
      return { items, error: null };
    } catch (err) {
      console.error('[wishlist] bootstrap', err);
      updateCountBadges(config.initialCount || 0);
      return { items: [], error: err };
    }
  }

  function escapeHtml(text) {
    return String(text ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function wishlistProductUrl(item) {
    const root = window.Shopify?.routes?.root || '/';
    if (item.handle) return `${root}products/${item.handle}`;
    if (item.url) return item.url;
    return '#';
  }

  function conversionBearMoneyHtml(cents) {
    const shopCurrency = window.theme?.settings?.shopCurrency || 'SAR';
    const normalizedCents = Math.round(Number(cents) || 0);
    let display = (normalizedCents / 100).toFixed(2);

    if (window.Shopify?.formatMoney && window.theme?.settings?.moneyFormat) {
      display = window.Shopify.formatMoney(normalizedCents, window.theme.settings.moneyFormat);
    }

    return (
      '<span class="money conversion-bear-money" data-currency="' +
      shopCurrency +
      '" data-currency-' +
      shopCurrency +
      '="' +
      normalizedCents +
      '">' +
      display +
      '</span>'
    );
  }

  function formatWishlistPriceHtml(item) {
    const amount = Number(item.price) || 0;
    const cents = item.priceInMajorUnits ? Math.round(amount * 100) : Math.round(amount);
    return conversionBearMoneyHtml(cents);
  }

  function refreshConversionBearMoney() {
    const convert = window.conversionBearAutoCurrencyConverter?.convertPricesOnPage;
    if (typeof convert !== 'function') return;

    function run() {
      convert.call(window.conversionBearAutoCurrencyConverter);
      if (!window.cbCurrencyIsLoaded) {
        window.setTimeout(run, 200);
      }
    }

    run();
    window.setTimeout(() => convert.call(window.conversionBearAutoCurrencyConverter), 500);
  }

  const ICON_REMOVE =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" focusable="false"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>';

  function renderWishlistPreviewCard(item) {
    const url = escapeHtml(wishlistProductUrl(item));
    const title = escapeHtml(item.title || '');
    const price = formatWishlistPriceHtml(item);
    const images = (item.images || []).filter(Boolean);
    const productGid = escapeHtml(item.gid || gidFromNumericId(item.numericId) || '');
    const handle = escapeHtml(item.handle || '');
    const removeLabel = escapeHtml(config.strings?.remove || 'Remove');
    const ariaRemove = escapeHtml(config.strings?.ariaRemove || removeLabel);
    const galleryHtml = images.length
      ? `<div class="wishlist-item__gallery" tabindex="0">
          <div class="wishlist-item__gallery-track">
            ${images
              .map(
                (src) =>
                  `<a class="wishlist-item__gallery-slide" href="${url}"><img src="${escapeHtml(src)}" alt="${title}" loading="lazy" width="100" height="125"></a>`
              )
              .join('')}
          </div>
        </div>`
      : '';

    return `
      <div
        class="cart-item wishlist-item"
        data-product-id="${item.numericId || ''}"
        data-product-gid="${productGid}"
        data-product-handle="${handle}"
        data-product-title="${title}"
      >
        <div class="cart-item__column cart-item__image">
          ${galleryHtml}
        </div>
        <div class="cart-item__not-image">
          <div class="cart-item__column cart-item__description">
            <div class="lightly-spaced-row">
              <div class="cart-item__title">
                <a class="text-current" href="${url}">${title}</a>
              </div>
            </div>
          </div>
          <div class="cart-item__column cart-item__price">
            <span class="theme-money cart-item__selling-price">${price}</span>
          </div>
          <div class="cart-item__column cart-item__quantity">
            <button
              type="button"
              class="cart-item__remove wishlist-item__remove"
              data-wishlist-remove
              aria-label="${ariaRemove}"
            >
              ${ICON_REMOVE}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  function updateWishlistPageCount(page, count) {
    const countEl = page.querySelector('[data-wishlist-page-count]');
    if (!countEl) return;

    if (count > 0) {
      countEl.hidden = false;
      countEl.textContent = countEl.dataset.countTemplate.replace('[count]', String(count));
    } else {
      countEl.hidden = true;
      countEl.textContent = '';
    }
  }

  function itemFromWishlistCard(card) {
    return {
      numericId: Number(card.dataset.productId) || null,
      gid: card.dataset.productGid || null,
      handle: card.dataset.productHandle || null,
      title: card.dataset.productTitle || '',
    };
  }

  async function handleWishlistRemove(page, card, item) {
    const button = card.querySelector('[data-wishlist-remove]');
    button?.classList.add('is-loading');
    if (button) button.disabled = true;

    try {
      await api.remove(item.gid);
      const [items, count] = await Promise.all([api.getList(), api.getCount()]);
      const productIds = productIdsFromItems(items);

      card.remove();
      syncAllToggles(productIds);
      updateCountBadges(count);
      updateWishlistPageCount(page, count);
      dispatchUpdated({ productId: item.numericId, saved: false, count, productIds });

      const list = page.querySelector('[data-wishlist-items]');
      if (!list?.querySelector('.wishlist-item')) {
        setWishlistPageState(page, 'empty');
      }
    } catch (err) {
      console.error('[wishlist] remove', err);
      if (button) button.disabled = false;
      button?.classList.remove('is-loading');
    }
  }

  function bindWishlistPageActions(page) {
    if (page.dataset.wishlistActionsBound === 'true') return;
    page.dataset.wishlistActionsBound = 'true';

    page.addEventListener('click', (event) => {
      const card = event.target.closest('.wishlist-item');
      if (!card || !page.contains(card)) return;

      const item = itemFromWishlistCard(card);

      if (event.target.closest('[data-wishlist-remove]')) {
        event.preventDefault();
        handleWishlistRemove(page, card, item);
      }
    });
  }

  function setWishlistPageState(page, state) {
    const loading = page.querySelector('[data-wishlist-page-loading]');
    const empty = page.querySelector('[data-wishlist-page-empty]');
    const error = page.querySelector('[data-wishlist-page-error]');
    const gridWrap = page.querySelector('[data-wishlist-page-grid]');

    if (loading) {
      loading.hidden = state !== 'loading';
      loading.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    }
    if (empty) empty.hidden = state !== 'empty';
    if (error) error.hidden = state !== 'error';
    if (gridWrap) gridWrap.hidden = state !== 'grid';
  }

  async function hydrateWishlistPage(result) {
    const page = document.querySelector('[data-wishlist-page]');
    if (!page) return;

    const items = result?.items || [];
    const error = result?.error;
    const gridWrap = page.querySelector('[data-wishlist-page-grid]');
    const list = gridWrap?.querySelector('[data-wishlist-items]');
    const countEl = page.querySelector('[data-wishlist-page-count]');
    const errorText = page.querySelector('[data-wishlist-page-error-text]');

    if (!list) return;

    setWishlistPageState(page, 'loading');
    list.innerHTML = '';

    if (error) {
      if (errorText) {
        errorText.textContent = error.message || config.strings?.error || 'Wishlist request failed';
      }
      setWishlistPageState(page, 'error');
      if (countEl) countEl.hidden = true;
      return;
    }

    if (!items.length) {
      setWishlistPageState(page, 'empty');
      if (countEl) countEl.hidden = true;
      return;
    }

    const validItems = items.filter((item) => item.title || item.handle);

    if (!validItems.length) {
      setWishlistPageState(page, 'empty');
      if (countEl) countEl.hidden = true;
      return;
    }

    const localizedItems = await localizeWishlistItems(validItems);
    const displayItems = localizedItems.filter((item) => item.title);

    if (!displayItems.length) {
      setWishlistPageState(page, 'empty');
      if (countEl) countEl.hidden = true;
      return;
    }

    list.innerHTML = displayItems.map(renderWishlistPreviewCard).join('');
    refreshConversionBearMoney();
    bindWishlistPageActions(page);
    setWishlistPageState(page, 'grid');

    if (countEl) {
      countEl.hidden = false;
      countEl.textContent = countEl.dataset.countTemplate.replace('[count]', String(displayItems.length));
    }
  }

  class WishlistToggle extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('[data-wishlist-button]');
      this.label = this.querySelector('[data-wishlist-label]');
      this.message = this.querySelector('[data-wishlist-message]');
      this.live = this.querySelector('[data-wishlist-status-live]');

      this.productId = Number(this.dataset.productId);
      this.productGid = this.dataset.productGid || gidFromNumericId(this.productId);
      this.loggedIn = this.dataset.loggedIn === 'true';
      this.customerId = this.dataset.customerId || config.customerId;
      this.customerEmail = this.dataset.customerEmail || config.customerEmail;

      this.labels = {
        save: this.dataset.labelSave || 'Save to wishlist',
        saved: this.dataset.labelSaved || 'Remove from wishlist',
        loading: this.dataset.labelLoading || 'Updating…',
      };

      this.aria = {
        save: this.dataset.ariaSave || 'Save to wishlist',
        saved: this.dataset.ariaSaved || 'Saved to wishlist',
        remove: this.dataset.ariaRemove || 'Remove from wishlist',
      };

      this.ids = parseIds(this.dataset.wishlistIds);
      if (this.loggedIn) {
        this.btn?.addEventListener('click', this.onClick.bind(this));
      }
      this.syncUi(false);
      this.syncSavedStateFromApi();
    }

    async syncSavedStateFromApi() {
      if (!this.loggedIn) return;

      const toggleApi = new WishlistApi({
        apiBase: config.apiBase,
        customerId: this.customerId,
        customerEmail: this.customerEmail,
        source: config.source,
      });

      if (!toggleApi.ready) return;

      try {
        const isWishlisted = await toggleApi.check(this.productGid);
        applyProductWishlistState(this.productId, isWishlisted, false);
      } catch (err) {
        console.error('[wishlist] check', err);
      }
    }

    get isSaved() {
      return this.ids.has(this.productId);
    }

    setMessage(text, isError) {
      if (!this.message) return;
      if (!text) {
        this.message.hidden = true;
        this.message.textContent = '';
        return;
      }
      this.message.hidden = false;
      this.message.textContent = text;
      this.message.classList.toggle('wishlist-toggle__message--error', !!isError);
    }

    syncUi(announce) {
      const saved = this.isSaved;
      this.btn?.setAttribute('aria-pressed', saved ? 'true' : 'false');
      this.btn?.classList.toggle('is-saved', saved);

      if (this.label) {
        this.label.textContent = saved ? this.labels.saved : this.labels.save;
      }

      const aria = saved ? this.aria.saved : this.aria.save;
      if (this.btn) this.btn.setAttribute('aria-label', saved ? this.aria.remove : aria);

      if (announce && this.live) {
        this.live.textContent = saved ? this.labels.saved : this.labels.save;
      }
    }

    setIdsFromResponse(productIds) {
      if (!Array.isArray(productIds)) return;
      this.ids = new Set(productIds.map(Number).filter((n) => !Number.isNaN(n)));
      this.dataset.wishlistIds = idsToCsv(this.ids);
      this.syncUi(true);
    }

    async onClick(event) {
      event.preventDefault();

      const toggleApi = new WishlistApi({
        apiBase: config.apiBase,
        customerId: this.customerId,
        customerEmail: this.customerEmail,
        source: config.source,
      });

      if (!toggleApi.ready) {
        this.setMessage(config.strings?.error, true);
        return;
      }

      const nextSaved = !this.isSaved;
      const previousSaved = this.isSaved;

      if (nextSaved) this.ids.add(this.productId);
      else this.ids.delete(this.productId);

      this.syncUi(true);
      this.setMessage('');
      this.btn?.classList.add('is-loading');
      this.btn.disabled = true;

      if (this.label) this.label.textContent = this.labels.loading;

      try {
        if (nextSaved) {
          await toggleApi.add(this.productGid);
        } else {
          await toggleApi.remove(this.productGid);
        }

        const [isWishlisted, count] = await Promise.all([
          toggleApi.check(this.productGid),
          toggleApi.getCount(),
        ]);

        applyProductWishlistState(this.productId, isWishlisted, true);

        dispatchUpdated({
          productId: this.productId,
          saved: isWishlisted,
          count,
        });
      } catch (err) {
        if (previousSaved) this.ids.add(this.productId);
        else this.ids.delete(this.productId);
        this.syncUi(true);

        let message = err.message || config.strings?.error;
        if (err.status === 404 && message === 'user_not_found') {
          message = config.strings?.error;
        }
        this.setMessage(message, true);
        console.error('[wishlist]', err);
      } finally {
        this.btn?.classList.remove('is-loading');
        this.btn.disabled = false;
        if (this.label) this.syncUi(false);
      }
    }
  }

  if (!customElements.get(SELECTOR_TOGGLE)) {
    customElements.define(SELECTOR_TOGGLE, WishlistToggle);
  }

  document.addEventListener('wishlist:updated', (event) => {
    const { productIds, productId } = event.detail || {};
    if (!productIds?.length && productId == null) return;

    document.querySelectorAll(SELECTOR_TOGGLE).forEach((el) => {
      if (productId != null && Number(el.dataset.productId) !== Number(productId)) return;
      if (productIds?.length) {
        el.dataset.wishlistIds = productIds.join(',');
        el.ids = parseIds(el.dataset.wishlistIds);
      }
      if (typeof el.syncUi === 'function') el.syncUi(false);
    });
  });

  updateCountBadges(config.initialCount || 0);

  const wishlistPage = document.querySelector('[data-wishlist-page]');
  if (wishlistPage) bindWishlistPageActions(wishlistPage);

  bootstrapWishlist().then((result) => {
    const page = document.querySelector('[data-wishlist-page]');
    if (!page) return;

    if (result?.skipHydration) {
      setWishlistPageState(page, 'empty');
      return;
    }

    hydrateWishlistPage(result || { items: [] });
  });
})();
